import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { hasDomain } from '../utils/authStorage';

const AlertWebSocketContext = createContext(null);
const DeviceWebSocketContext = createContext(null);

function createWebSocketProvider(WebSocketContext, wsPath) {
  return function WebSocketProvider({ children }) {
    const wsRef = useRef(null);
    const subscribersRef = useRef(new Set());
    const reconnectTimerRef = useRef(null);
    const retryCountRef = useRef(0);
    const [isConnected, setIsConnected] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
      mountedRef.current = true;
      return () => { mountedRef.current = false; };
    }, []);

    const connect = useCallback(() => {
      const baseWs = process.env.REACT_APP_WS_URL;
      if (!baseWs) return;
      if (!hasDomain('surveillance')) return;
      const wsUrl = baseWs.endsWith('/')
        ? `${baseWs}ws${wsPath}/`
        : `${baseWs}/ws${wsPath}/`;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!mountedRef.current) { ws.close(); return; }
          retryCountRef.current = 0;
          setIsConnected(true);
        };

        ws.onmessage = (event) => {
          if (!hasDomain('surveillance')) return;
          try {
            const msg = JSON.parse(event.data);
            subscribersRef.current.forEach(cb => cb(msg));
          } catch (e) {
            console.warn('WS parse error', e);
          }
        };

        ws.onclose = () => {
          setIsConnected(false);
          if (!mountedRef.current) return;
          retryCountRef.current += 1;
          const delay = Math.min(30000, 1000 * Math.pow(2, retryCountRef.current));
          reconnectTimerRef.current = setTimeout(connect, delay);
        };

        ws.onerror = () => {
          try { ws.close(); } catch (e) { }
        };
      } catch (e) {
        console.error('WS setup failed', e);
      }
    }, []);

    useEffect(() => {
      connect();
      return () => {
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        if (wsRef.current) { try { wsRef.current.close(); } catch (e) { } }
        wsRef.current = null;
      };
    }, [connect]);

    const subscribe = useCallback((cb) => {
      const wrapped = (msg) => {
        if (!hasDomain('surveillance')) return;
        cb(msg);
      };
      subscribersRef.current.add(wrapped);
      return () => { subscribersRef.current.delete(wrapped); };
    }, []);

    const subscribeTyped = useCallback((typeFilter, cb) => {
      const wrapper = (msg) => {
        if (!hasDomain('surveillance')) return;
        if (typeFilter.includes(msg?.type)) {
          cb(msg);
        }
      };
      subscribersRef.current.add(wrapper);
      return () => { subscribersRef.current.delete(wrapper); };
    }, []);

    const sendMessage = useCallback((msg) => {
      if (!hasDomain('surveillance')) return false;
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(msg));
        return true;
      }
      return false;
    }, []);

    return (
      <WebSocketContext.Provider value={{ isConnected, subscribe, subscribeTyped, sendMessage }}>
        {children}
      </WebSocketContext.Provider>
    );
  };
}

export const AlertWebSocketProvider = createWebSocketProvider(AlertWebSocketContext, '/alerts/updates');
export const DeviceWebSocketProvider = createWebSocketProvider(DeviceWebSocketContext, '/devices/updates');

export function useAlertWebSocket() {
  const ctx = useContext(AlertWebSocketContext);
  if (!ctx) throw new Error('useAlertWebSocket must be used within AlertWebSocketProvider');
  return ctx;
}

export function useDeviceWebSocket() {
  const ctx = useContext(DeviceWebSocketContext);
  if (!ctx) throw new Error('useDeviceWebSocket must be used within DeviceWebSocketProvider');
  return ctx;
}
